import app from 'app';
import {saveAs} from 'fileSaver';
import JSZip from 'jszip';
import XLSX from 'xlsx';

app.service('SeospiderBulkExportService', SeospiderBulkExportService);
SeospiderBulkExportService.$inject = ['$q', '$window', 'ExportService'];

function SeospiderBulkExportService($q, $window, ExportService) {
    var PAGE_SIZE = 1000;

    return {
        exportCsv: exportCsv,
        exportXlsx: exportXlsx,
        exportZip: exportZip,
    };

    function ensureActive(token) {
        if (token && token.cancelled) {
            throw {cancelled: true};
        }
    }

    function fetchAll(load, onProgress, token) {
        var rows = [];
        var offset = 0;

        function next() {
            ensureActive(token);
            return $q.when(load(offset, PAGE_SIZE)).then(function (response) {
                ensureActive(token);
                var page = response && Array.isArray(response.data) ? response.data : [];
                rows = rows.concat(page);
                var hasTotal = response && response.totalRows !== null
                    && response.totalRows !== undefined && response.totalRows !== '';
                var total = hasTotal ? Number(response.totalRows) : null;
                if (total !== null && !isFinite(total)) {
                    hasTotal = false;
                    total = null;
                }
                if (onProgress) onProgress(rows.length, total);
                var hasMore = hasTotal
                    ? page.length > 0 && offset + page.length < total
                    : page.length === PAGE_SIZE;
                if (!hasMore) return rows;
                offset += page.length;
                return next();
            });
        }

        return next();
    }

    function loadEntry(entry, onProgress, token) {
        return fetchAll(entry.load, onProgress, token).then(function (rows) {
            ensureActive(token);
            return entry.transform ? entry.transform(rows) : rows;
        });
    }

    function exportCsv(entry, onProgress, token) {
        var handlePromise = pickFile(entry.fileName + '.csv', 'text/csv', '.csv');
        if (handlePromise) {
            return handlePromise.then(function (handle) {
                return ExportService.checkExportAvailability()
                    .then(function () { return streamCsv(entry, handle, onProgress, token); });
            });
        }
        return ExportService.checkExportAvailability()
            .then(function () { return loadEntry(entry, onProgress, token); })
            .then(function (rows) {
                saveAs(csvBlob(rows), safeName(entry.fileName) + '.csv');
                return rows.length;
            });
    }

    function exportXlsx(entry, onProgress, token) {
        return ExportService.checkExportAvailability()
            .then(function () { return loadEntry(entry, onProgress, token); })
            .then(function (rows) {
                var workbook = XLSX.utils.book_new();
                var sheet = rows.length
                    ? XLSX.utils.json_to_sheet(rows)
                    : XLSX.utils.aoa_to_sheet([]);
                XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName(entry.name));
                var data = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
                saveAs(new Blob([data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }), safeName(entry.fileName) + '.xlsx');
                return rows.length;
            });
    }

    function exportZip(entries, fileName, onProgress, token) {
        return ExportService.checkExportAvailability().then(function () {
            var zip = new JSZip();
            var completed = [];

            function next(index) {
                ensureActive(token);
                if (index >= entries.length) {
                    return zip.generateAsync({type: 'blob'}).then(function (blob) {
                        ensureActive(token);
                        saveAs(blob, safeName(fileName) + '.zip');
                        return completed;
                    });
                }
                var entry = entries[index];
                return loadEntry(entry, function (loaded, total) {
                    if (onProgress) {
                        onProgress({
                            index: index,
                            count: entries.length,
                            name: entry.name,
                            loaded: loaded,
                            total: total
                        });
                    }
                }, token).then(function (rows) {
                    zip.file(safeName(entry.name) + '.csv', csvContent(rows));
                    completed.push({name: entry.name, rows: rows.length});
                    return next(index + 1);
                });
            }

            return next(0);
        });
    }

    function pickFile(fileName, mimeType, extension) {
        if (!$window.showSaveFilePicker || !$window.isSecureContext) return null;
        try {
            return $window.showSaveFilePicker({
                suggestedName: safeName(fileName),
                types: [{
                    description: extension.replace('.', '').toUpperCase() + ' file',
                    accept: {[mimeType]: [extension]},
                }],
            }).catch(function (error) {
                if (error && error.name === 'AbortError') throw {cancelled: true};
                throw error;
            });
        } catch (error) {
            if (error && error.name === 'AbortError') return $q.reject({cancelled: true});
            return null;
        }
    }

    async function streamCsv(entry, handle, onProgress, token) {
        var writable;
        var offset = 0;
        var rowsWritten = 0;
        var columns = null;
        var headerWritten = false;
        try {
            writable = await handle.createWritable();
            await writable.write('\ufeff');
            while (true) {
                ensureActive(token);
                var response = await entry.load(offset, PAGE_SIZE);
                ensureActive(token);
                var page = response && Array.isArray(response.data) ? response.data : [];
                var transformed = entry.transform ? entry.transform(page) : page;
                if (!columns) columns = collectColumns(transformed);
                if (columns && !headerWritten) {
                    await writable.write(columns.map(csvValue).join(',') + '\r\n');
                    headerWritten = true;
                }
                if (transformed.length) {
                    await writable.write(transformed.map(function (row) {
                        return csvLine(row, columns);
                    }).join('\r\n') + '\r\n');
                }
                rowsWritten += transformed.length;
                var total = response && response.totalRows !== null
                    && response.totalRows !== undefined ? Number(response.totalRows) : null;
                if (onProgress) onProgress(rowsWritten, isFinite(total) ? total : null);
                var hasMore = isFinite(total)
                    ? page.length > 0 && offset + page.length < total
                    : page.length === PAGE_SIZE;
                if (!hasMore) break;
                offset += page.length;
            }
            await writable.close();
            return rowsWritten;
        } catch (error) {
            if (writable) {
                try { await writable.abort(); } catch (err) {}
            }
            throw error;
        }
    }

    function csvBlob(rows) {
        return new Blob(['\ufeff', csvContent(rows)], {type: 'text/csv;charset=utf-8'});
    }

    function csvContent(rows) {
        var list = Array.isArray(rows) ? rows : [];
        var columns = collectColumns(list);
        var lines = [columns.map(csvValue).join(',')];
        list.forEach(function (row) {
            lines.push(csvLine(row, columns));
        });
        return lines.join('\r\n');
    }

    function collectColumns(rows) {
        var columns = [];
        var seen = {};
        (Array.isArray(rows) ? rows : []).forEach(function (row) {
            Object.keys(row || {}).forEach(function (key) {
                if (!seen[key]) {
                    seen[key] = true;
                    columns.push(key);
                }
            });
        });
        return columns;
    }

    function csvLine(row, columns) {
        return (columns || []).map(function (column) {
            return csvValue(row && row[column]);
        }).join(',');
    }

    function csvValue(value) {
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') value = JSON.stringify(value);
        var text = String(value).replace(/"/g, '""');
        return /[",\r\n]/.test(text) ? '"' + text + '"' : text;
    }

    function safeName(value) {
        return String(value || 'export').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
    }

    function safeSheetName(value) {
        return safeName(value).replace(/[\[\]]/g, ' ').slice(0, 31) || 'Export';
    }
}
